import type { NoteDto } from '@keep/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useCurrentUser } from '../../api/auth';
import { useDeleteAttachment, useNote } from '../../api/notes';
import { Dialog } from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/spinner';
import { ApiError } from '../../lib/api';
import { noteColorVar } from '../../lib/colors';
import { formatEdited, formatFull } from '../../lib/dates';
import { toast } from '../../stores/toasts';
import { AttachmentGrid } from './attachment-grid';
import { CollaboratorsDialog } from './collaborators-dialog';
import { isDraftEmpty, toggleChecklist } from './draft';
import { CollaboratorAvatars, NoteChips } from './note-chips';
import { NoteForm } from './note-form';
import { NoteToolbar } from './note-toolbar';
import { useAutosave } from './use-autosave';
import { useImageUpload } from './use-image-upload';
import { useNoteActions } from './use-note-actions';

function EditorBody({
  note,
  onClose,
  registerClose,
}: {
  note: NoteDto;
  onClose: () => void;
  /** Permet à la boîte de dialogue (Échap, clic à l'extérieur) de passer par la même fermeture. */
  registerClose: (close: () => void) => void;
}) {
  const me = useCurrentUser();
  const actions = useNoteActions();
  const uploadImages = useImageUpload();
  const deleteAttachment = useDeleteAttachment();
  const { draft, change, flush } = useAutosave(note);
  const [collaboratorsOpen, setCollaboratorsOpen] = useState(false);
  const trashed = note.trashedAt !== null;

  const close = useCallback(() => {
    flush();
    // Keep supprime silencieusement une note vidée de son contenu.
    if (!trashed && note.owner.id === me.id && isDraftEmpty(draft, note.attachments.length)) {
      actions.trash([note]);
      toast('Note vide supprimée');
    }
    onClose();
  }, [flush, trashed, note, me.id, draft, actions, onClose]);

  useEffect(() => registerClose(close), [registerClose, close]);

  return (
    <>
      <div className="scrollbar-thin flex-1 overflow-y-auto" data-note-form>
        <AttachmentGrid
          attachments={note.attachments}
          onRemove={
            trashed
              ? undefined
              : (a) => deleteAttachment.mutate({ noteId: note.id, attachmentId: a.id })
          }
        />
        <NoteForm draft={draft} onChange={change} readOnly={trashed} />
        <div className="flex flex-col gap-2 px-4 pb-2">
          <NoteChips
            note={draft}
            onRemoveReminder={trashed ? undefined : () => change({ reminderAt: null })}
            onRemoveLabel={
              trashed
                ? undefined
                : (labelId) => change({ labelIds: draft.labelIds.filter((id) => id !== labelId) })
            }
          />
          <CollaboratorAvatars
            note={note}
            onClick={trashed ? undefined : () => setCollaboratorsOpen(true)}
          />
          <div
            className="flex justify-end text-xs text-muted"
            title={`Créée le ${formatFull(note.createdAt)}`}
          >
            {trashed ? 'Note dans la corbeille' : `Modifiée : ${formatEdited(note.updatedAt)}`}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <NoteToolbar
          color={draft.color}
          reminderAt={draft.reminderAt}
          labelIds={draft.labelIds}
          archived={draft.archived}
          type={draft.type}
          trashed={trashed}
          onReminder={(reminderAt) => change({ reminderAt })}
          onCollaborators={() => setCollaboratorsOpen(true)}
          onColor={(color) => change({ color })}
          onAddImages={(files) => uploadImages(note.id, files)}
          onArchive={() => {
            flush();
            if (draft.archived) actions.unarchive([note]);
            else actions.archive([note]);
            onClose();
          }}
          onTrash={() => {
            flush();
            actions.trash([note]);
            onClose();
          }}
          onToggleLabel={(labelId, enabled) =>
            change({
              labelIds: enabled
                ? [...draft.labelIds, labelId]
                : draft.labelIds.filter((id) => id !== labelId),
            })
          }
          onCopy={() => {
            flush();
            actions.copy([note]);
          }}
          onToggleChecklist={() => change(toggleChecklist(draft))}
          onRestore={() => actions.restore([note])}
          onDeleteForever={() => {
            actions.deleteForever([note]);
            onClose();
          }}
        />
        <button
          type="button"
          onClick={close}
          className="h-9 shrink-0 rounded px-6 text-sm font-medium hover:bg-hover"
        >
          Fermer
        </button>
      </div>
      {collaboratorsOpen && (
        <CollaboratorsDialog note={note} onClose={() => setCollaboratorsOpen(false)} />
      )}
      <CloseShortcut onClose={close} />
    </>
  );
}

/** Ctrl/Cmd + Entrée ferme la note, comme dans Keep. */
function CloseShortcut({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return null;
}

/** Éditeur ouvert par le paramètre d'URL ?note=<id> : un lien vers une note est partageable entre onglets. */
export function NoteEditorRoute() {
  const [params, setParams] = useSearchParams();
  const noteId = params.get('note');
  const { data: note, error, isPending } = useNote(noteId);
  const editorClose = useRef<(() => void) | null>(null);
  const registerClose = useCallback((fn: () => void) => {
    editorClose.current = fn;
  }, []);

  const close = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('note');
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  useEffect(() => {
    if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
      toast('Cette note n’existe plus');
      close();
    }
  }, [error, close]);

  if (!noteId) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) (editorClose.current ?? close)();
      }}
      title={note?.title || 'Note'}
      hideTitle
      // Plein écran sur mobile, comme l'application Keep.
      className="max-w-[600px] max-sm:h-dvh max-sm:max-h-none max-sm:w-screen max-sm:rounded-none max-sm:border-0"
      style={note ? { backgroundColor: noteColorVar(note.color) } : undefined}
      onOpenAutoFocus={(event) => event.preventDefault()}
    >
      {note ? (
        <EditorBody key={note.id} note={note} onClose={close} registerClose={registerClose} />
      ) : isPending ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      ) : null}
    </Dialog>
  );
}
